import { useTranslation } from 'react-i18next'
import { TextField, Typogaphy } from 'vnetwork-ui'
import {
    Mail,
    GripVertical,
    ArrowRight,
    ArrowLeft,
    Variable,
} from 'lucide-react'
import { MailerTemplateVariableItem } from '../../../types'

interface VariableMappingProps {
    activeVariables: MailerTemplateVariableItem[]
    sharedVariables: MailerTemplateVariableItem[]
    receiverVariables: MailerTemplateVariableItem[]
    customerVariableNames: string[]
    sharedVariableValues: Record<string, string>
    importedColumns: string[]
    receivers: Record<string, string | undefined>[]
    draggedVariable: string | null
    onDragStart: (varName: string) => void
    onDragOver: (e: React.DragEvent) => void
    onDropToShared: (e: React.DragEvent) => void
    onDropToCustomer: (e: React.DragEvent) => void
    onMoveToCustomer: (varName: string) => void
    onMoveToShared: (varName: string) => void
    onSharedValueChange: (varName: string, value: string) => void
    // Disable variable movement (for internal source mode)
    disableVariableMovement?: boolean
}

const VariableMapping: React.FC<VariableMappingProps> = ({
    activeVariables,
    sharedVariables,
    receiverVariables,
    customerVariableNames,
    sharedVariableValues,
    importedColumns,
    receivers,
    draggedVariable,
    onDragStart,
    onDragOver,
    onDropToShared,
    onDropToCustomer,
    onMoveToCustomer,
    onMoveToShared,
    onSharedValueChange,
    disableVariableMovement = false,
}) => {
    const { t } = useTranslation()

    // Check if a variable has data in receivers (either by importedColumns or actual data)
    const hasReceiverData = (varName: string): boolean => {
        // Check in importedColumns first
        if (importedColumns.includes(varName)) return true

        // Check if any receiver has data for this variable
        if (receivers.length > 0) {
            const firstReceiver = receivers[0]
            // Check exact key
            if (firstReceiver[varName]) return true
            // Check lowercase key
            if (firstReceiver[varName.toLowerCase()]) return true
        }
        return false
    }

    // Always show if there are active variables OR if we need to show the required Email field
    // Email is always required for receivers even if not used as a variable in content

    return (
        <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b flex items-center gap-2">
                <Variable size={18} className="text-purple-600" />
                <Typogaphy className="text-sm font-semibold text-gray-700">
                    {t('mailerCampaign:variable_mapping')} (
                    {activeVariables.length} {t('mailerCampaign:variables')})
                </Typogaphy>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x">
                {/* Shared Variables Section */}
                <div
                    className={`p-4 ${!disableVariableMovement && draggedVariable && customerVariableNames.includes(draggedVariable) ? 'bg-blue-50' : 'bg-white'}`}
                    onDragOver={
                        disableVariableMovement ? undefined : onDragOver
                    }
                    onDrop={
                        disableVariableMovement ? undefined : onDropToShared
                    }
                >
                    <div className="flex items-center justify-between mb-3">
                        <Typogaphy className="text-sm font-medium text-blue-700">
                            {t('mailerCampaign:shared_variables')} (
                            {sharedVariables.length})
                        </Typogaphy>
                    </div>

                    <div className="space-y-2">
                        {sharedVariables.map((variable) => (
                            <div
                                key={variable.name}
                                draggable={!disableVariableMovement}
                                onDragStart={
                                    disableVariableMovement
                                        ? undefined
                                        : () => onDragStart(variable.name)
                                }
                                className={`bg-blue-50 border border-blue-200 rounded-md px-4 py-2 ${disableVariableMovement ? '' : 'cursor-grab active:cursor-grabbing'} hover:shadow-sm transition-shadow`}
                            >
                                <div className="flex items-center gap-2">
                                    {!disableVariableMovement && (
                                        <GripVertical
                                            size={14}
                                            className="text-gray-400 flex-shrink-0"
                                        />
                                    )}
                                    <div className="flex-1">
                                        <TextField
                                            size="small"
                                            label={variable.label}
                                            value={
                                                sharedVariableValues[
                                                    variable.name
                                                ] || ''
                                            }
                                            onChange={(e) =>
                                                onSharedValueChange(
                                                    variable.name,
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                variable.description ||
                                                `${t('mailerCampaign:enter')} ${variable.label}`
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    {!disableVariableMovement && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                onMoveToCustomer(variable.name)
                                            }
                                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 flex-shrink-0"
                                            title={t(
                                                'mailerCampaign:move_to_customer'
                                            )}
                                        >
                                            <ArrowRight size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        {sharedVariables.length === 0 && (
                            <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                                {t('mailerCampaign:drag_variables_here')}
                            </div>
                        )}
                    </div>
                </div>

                {/* Customer Variables Section */}
                <div
                    className={`p-4 min-h-[200px] ${!disableVariableMovement && draggedVariable && !customerVariableNames.includes(draggedVariable) ? 'bg-green-50' : 'bg-white'}`}
                    onDragOver={
                        disableVariableMovement ? undefined : onDragOver
                    }
                    onDrop={
                        disableVariableMovement ? undefined : onDropToCustomer
                    }
                >
                    <div className="flex items-center justify-between mb-3">
                        <Typogaphy className="text-sm font-medium text-green-700">
                            {t('mailerCampaign:customer_variables')} (
                            {receiverVariables.filter((v) => v.name !== 'email')
                                .length + 1}
                            )
                        </Typogaphy>
                        {!disableVariableMovement && (
                            <span className="text-xs text-gray-400">
                                {t('mailerCampaign:from_import_file')}
                            </span>
                        )}
                    </div>

                    <div className="space-y-2">
                        {/* Email */}
                        <div
                            className={`rounded-md px-4 py-2 ${hasReceiverData('email') ? 'bg-green-100 border border-green-300' : 'bg-orange-50 border border-orange-200'}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Mail
                                        size={14}
                                        className={
                                            hasReceiverData('email')
                                                ? 'text-green-600'
                                                : 'text-orange-500'
                                        }
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        Email
                                    </span>
                                </div>
                                <code
                                    className={`text-xs px-1.5 py-0.5 rounded ${hasReceiverData('email') ? 'bg-green-200 text-green-700' : 'bg-orange-200 text-orange-700'}`}
                                >
                                    email
                                </code>
                            </div>
                        </div>

                        {/* Filter out email since it's shown above */}
                        {receiverVariables
                            .filter((v) => v.name !== 'email')
                            .map((variable) => {
                                const hasData = hasReceiverData(variable.name)
                                return (
                                    <div
                                        key={variable.name}
                                        draggable={!disableVariableMovement}
                                        onDragStart={
                                            disableVariableMovement
                                                ? undefined
                                                : () =>
                                                      onDragStart(variable.name)
                                        }
                                        className={`rounded-lg p-3 ${disableVariableMovement ? '' : 'cursor-grab active:cursor-grabbing'} hover:shadow-sm transition-shadow ${hasData ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                {!disableVariableMovement && (
                                                    <GripVertical
                                                        size={14}
                                                        className="text-gray-400"
                                                    />
                                                )}
                                                <span className="text-sm font-medium text-gray-700">
                                                    {variable.label}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <code
                                                    className={`text-xs px-1.5 py-0.5 rounded ${hasData ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}
                                                >
                                                    {variable.name}
                                                </code>
                                                {!disableVariableMovement && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            onMoveToShared(
                                                                variable.name
                                                            )
                                                        }
                                                        className={`text-xs flex items-center gap-1 ${hasData ? 'text-green-600 hover:text-green-800' : 'text-orange-600 hover:text-orange-800'}`}
                                                        title={t(
                                                            'move_to_shared'
                                                        )}
                                                    >
                                                        <ArrowLeft size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}

                        {receiverVariables.filter((v) => v.name !== 'email')
                            .length === 0 && (
                            <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                                {t('mailerCampaign:drag_variables_here')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default VariableMapping
